import { z } from 'zod';
import { sqlDateTimeParser, transformSQLDateTime } from '../core/phpTypes';
import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { atomWithForage } from '../core/storage';

const createParser = z.object({
	userId: z.number(),
	action: z.literal('create_reservation'),
	details: z.object({
		id: z.number(),
		locationId: z.coerce.number().optional(),
		start: sqlDateTimeParser.transform(transformSQLDateTime),
		end: sqlDateTimeParser.transform(transformSQLDateTime),
		userIds: z.array(z.coerce.number()),
	}),
});
const updateParser = z.object({
	userId: z.number(),
	action: z.literal('update_reservation'),
	details: z.object({
		old: z.object({
			locationId: z.coerce.number().optional(),
			start: sqlDateTimeParser.transform(transformSQLDateTime),
			end: sqlDateTimeParser.transform(transformSQLDateTime),
			users: z.array(
				z.object({
					userId: z.number(),
					accepted: z.boolean(),
				}),
			),
		}),
		new: z.object({
			locationId: z.coerce.number().optional(),
			start: sqlDateTimeParser.transform(transformSQLDateTime),
			end: sqlDateTimeParser.transform(transformSQLDateTime),
		}),
		removeUsers: z.array(z.coerce.number()),
		addUsers: z.array(z.coerce.number()),
	}),
});

const cancelParser = z.object({
	userId: z.number(),
	action: z.literal('cancel_reservation'),
	details: z.object({
		locationId: z.number().optional(),
		start: sqlDateTimeParser.transform(transformSQLDateTime),
		end: sqlDateTimeParser.transform(transformSQLDateTime),
		users: z.array(
			z.object({
				userId: z.number(),
				accepted: z.boolean(),
			}),
		),
	}),
});

const acceptedParser = z.object({
	userId: z.number(),
	action: z.literal('accept_invitation'),
	details: z.object({
		locationId: z.number().optional(),
		start: sqlDateTimeParser.transform(transformSQLDateTime),
		end: sqlDateTimeParser.transform(transformSQLDateTime),
		users: z.array(
			z.object({
				userId: z.number(),
				accepted: z.boolean(),
			}),
		),
	}),
});

const approvedParser = z.object({
	userId: z.number(),
	action: z.literal('approved_reservation'),
	details: z.object({
		locationId: z.number().optional(),
		start: sqlDateTimeParser.transform(transformSQLDateTime),
		end: sqlDateTimeParser.transform(transformSQLDateTime),
		users: z.array(
			z.object({
				userId: z.number(),
				accepted: z.boolean(),
			}),
		),
	}),
});

export const notificationDetailsParser = z.union([
	createParser,
	updateParser,
	cancelParser,
	acceptedParser,
	approvedParser,
]);
export type FocusResaNotificationDetails = z.infer<
	typeof notificationDetailsParser
>;

export const notificationParser = z.object({
	id: z.number(),
	created: sqlDateTimeParser.transform(transformSQLDateTime),
	updated: sqlDateTimeParser.transform(transformSQLDateTime),
	recipientId: z.number(),
	read: z.boolean(),
	event: z.string().transform((rawEvent) => {
		const o = JSON.parse(rawEvent);
		if (!('action' in o))
			throw new Error('Invalid object after deserialization');
		try {
			switch (o.action) {
				case 'create_reservation':
					return createParser.parse(o);
				case 'update_reservation':
					return updateParser.parse(o);
				case 'cancel_reservation':
					return cancelParser.parse(o);
				case 'accept_invitation':
					return acceptedParser.parse(o);
				case 'approved_reservation':
					return approvedParser.parse(o);
			}
		} catch (e) {
			console.error(o, e.issues);
		}
		throw new Error(`Invalid action ${o.action}`);
	}), //.pipe(notificationDetailsParser)
});
export type FocusResaNotification = z.infer<typeof notificationParser>;

export const allNotificationsAtom = atomWithForage(
	'focus-resa-notifications',
	[] as FocusResaNotification[],
);
export const userNotifications = atomFamily((userId: number) =>
	atom((get) =>
		get(allNotificationsAtom).filter((n) => n.recipientId == userId),
	),
);

export const mergeNotifications = (
	prev: FocusResaNotification[],
	add: FocusResaNotification[],
) => {
	return [...prev.filter((n) => !add.some((n2) => n2.id === n.id)), ...add];
};
